import { fetchTrailersUser } from './trailers';
import { getCookie } from '../utils/csrf';

// Constants
const SET_MOVES = 'moves/SET_MOVES';
const ADD_MOVE = 'moves/ADD_MOVE';
const UPDATE_MOVE = 'moves/UPDATE_MOVE';
const REMOVE_MOVE = 'moves/REMOVE_MOVE';
const ACCEPT_MOVE = 'moves/ACCEPT_MOVE';
const FINISH_MOVE = 'moves/FINISH_MOVE';

// Action Creators
export const setMoves = (moves) => ({
  type: SET_MOVES,
  payload: moves,
});

export const addMove = (move) => ({
  type: ADD_MOVE,
  payload: move,
});

export const updateMove = (move) => ({
  type: UPDATE_MOVE,
  payload: move,
});

export const removeMove = (moveId) => ({
  type: REMOVE_MOVE,
  payload: moveId,
});

export const acceptMoveAction = (move) => ({
  type: ACCEPT_MOVE,
  payload: move,
});

export const finishMoveAction = (move) => ({
  type: FINISH_MOVE,
  payload: move,
});

// Thunks
export const fetchMoves = (accountId = null) => async (dispatch) => {
  try {
    let url = '/api/moves?sort=all_times&order=desc';
    if (accountId) {
      url += `&account_id=${accountId}`;
    }

    const response = await fetch(url, {
      headers: {
        'X-CSRF-TOKEN': getCookie('csrf_token')
      },
      credentials: 'include'
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || 'Failed to fetch moves');
    }

    const moves = await response.json();
    dispatch(setMoves(moves));
    return moves;
  } catch (error) {
    console.error('Error fetching moves:', error);
    // Dispatch empty array to ensure store is updated even on error
    dispatch(setMoves([]));
    return [];
  }
};

export const createMove = (moveData) => async (dispatch) => {
  try {
    const response = await fetch('/api/moves', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-CSRF-TOKEN': getCookie('csrf_token')
      },
      credentials: 'include',
      body: JSON.stringify(moveData),
    });

    const data = await response.json();

    if (!response.ok) {
      if (response.status === 403) {
        throw new Error("Permission denied: Only managers and administrators can create moves");
      }
      // Specific handling for trailer already in a requested move
      if (data.error && data.error.includes("already in a requested move")) {
        throw new Error(data.error);
      }
      throw new Error(data.error || 'Failed to create move');
    }

    dispatch(addMove(data));
    return data;
  } catch (error) {
    console.error('Error creating move:', error);
    throw error;
  }
};

export const acceptMove = (moveId) => async (dispatch) => {
  try {
    const response = await fetch(`/api/moves/${moveId}/accept`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        'X-CSRF-TOKEN': getCookie('csrf_token')
      },
      credentials: 'include',
      body: JSON.stringify({})
    });

    // Check if response is JSON before parsing
    const contentType = response.headers.get('content-type');
    if (!contentType || !contentType.includes('application/json')) {
      const text = await response.text();
      console.error('Non-JSON response received:', text.substring(0, 500));
      console.error('Full response status:', response.status);
      console.error('Response headers:', Object.fromEntries(response.headers.entries()));
      throw new Error(`Server error: Expected JSON response but got ${contentType || 'unknown content type'}. Check console for details.`);
    }

    const data = await response.json();

    if (!response.ok) {
      if (response.status === 403) {
        throw new Error("Permission denied: " + (data.error || "You can only accept moves for your own account"));
      }
      if (response.status === 409) {
        // For conflict errors, provide the exact error from the server
        throw new Error(data.error || 'This move cannot be accepted due to a conflict');
      }
      throw new Error(data.error || 'Failed to accept move');
    }

    console.log('Accept move response:', data);
    dispatch(acceptMoveAction(data));

    // Small delay to ensure backend updates are complete
    await new Promise(resolve => setTimeout(resolve, 200));

    // Refresh trailers state with the account_id from the move
    if (data.account_id) {
      await dispatch(fetchTrailersUser(data.account_id));
    }

    return data;
  } catch (error) {
    console.error('Error accepting move:', error);
    throw error;
  }
};

export const finishMove = (moveId) => async (dispatch) => {
  try {
    const response = await fetch(`/api/moves/${moveId}/finish`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        'X-CSRF-TOKEN': getCookie('csrf_token')
      },
      credentials: 'include',
      body: JSON.stringify({})
    });

    // Check if response is JSON before parsing
    const contentType = response.headers.get('content-type');
    if (!contentType || !contentType.includes('application/json')) {
      const text = await response.text();
      console.error('Non-JSON response received:', text.substring(0, 500));
      console.error('Full response status:', response.status);
      console.error('Response headers:', Object.fromEntries(response.headers.entries()));
      throw new Error(`Server error: Expected JSON response but got ${contentType || 'unknown content type'}. Check console for details.`);
    }

    const data = await response.json();

    if (!response.ok) {
      if (response.status === 403) {
        throw new Error("Permission denied: " + (data.error || "You can only finish moves for your own account"));
      }
      throw new Error(data.error || 'Failed to finish move');
    }

    // Update the move in the store
    dispatch(finishMoveAction(data));

    // Small delay to ensure backend updates are complete
    await new Promise(resolve => setTimeout(resolve, 200));

    // Refresh trailers state with the account_id from the move
    if (data.account_id) {
      await dispatch(fetchTrailersUser(data.account_id));
    }

    return data;
  } catch (error) {
    console.error('Error finishing move:', error);
    throw error;
  }
};

export const cancelMove = (moveId) => async (dispatch) => {
  try {
    const response = await fetch(`/api/moves/${moveId}/cancel`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        'X-CSRF-TOKEN': getCookie('csrf_token')
      },
      credentials: 'include',
      body: JSON.stringify({})
    });

    // Check if response is JSON before parsing
    const contentType = response.headers.get('content-type');
    if (!contentType || !contentType.includes('application/json')) {
      const text = await response.text();
      console.error('Non-JSON response received:', text.substring(0, 500));
      console.error('Full response status:', response.status);
      console.error('Response headers:', Object.fromEntries(response.headers.entries()));
      throw new Error(`Server error: Expected JSON response but got ${contentType || 'unknown content type'}. Check console for details.`);
    }

    const data = await response.json();

    if (!response.ok) {
      if (response.status === 403) {
        throw new Error("Permission denied: " + (data.error || "You can only cancel moves for your own account"));
      }
      throw new Error(data.error || 'Failed to cancel move');
    }

    dispatch(updateMove(data));

    // Small delay to ensure backend updates are complete
    await new Promise(resolve => setTimeout(resolve, 200));

    // Refresh trailers state with the account_id from the move
    if (data.account_id) {
      await dispatch(fetchTrailersUser(data.account_id));
    }

    return data;
  } catch (error) {
    console.error('Error cancelling move:', error);
    throw error;
  }
};

// Initial State
const initialState = {
  list: [],
  current: null,
};

// Reducer
export default function reducer(state = initialState, action) {
  switch (action.type) {
    case SET_MOVES:
      return {
        ...state,
        list: action.payload,
      };
    case ADD_MOVE:
      return {
        ...state,
        list: [...state.list, action.payload],
      };
    case UPDATE_MOVE:
    case ACCEPT_MOVE:
    case FINISH_MOVE:
      console.log('Reducer - Before update:', state.list);
      console.log('Reducer - Updating move:', action.payload);
      const updatedState = {
        ...state,
        list: state.list.map((move) =>
          move.id === action.payload.id ? action.payload : move
        ),
      };
      console.log('Reducer - After update:', updatedState.list);
      return updatedState;
    case REMOVE_MOVE:
      return {
        ...state,
        list: state.list.filter((move) => move.id !== action.payload),
      };
    default:
      return state;
  }
}
